import { formatScheduled, RescheduleOptions } from '../../services/ordersApi';
import type { RescheduleError } from './rescheduleErrors';

export interface RescheduleForm {
  date: string;
  pickupSlotId: string;
  returnDate: string;
  returnSlotId: string;
  garageAgreed: boolean;
  note: string;
}

interface Props {
  options: RescheduleOptions;
  form: RescheduleForm;
  onChange: (patch: Partial<RescheduleForm>) => void;
  today: string;
  error: RescheduleError | null;
  checking: boolean;
  disabled: boolean;
}

function FieldError({ id, error, field }: { id: string; error: RescheduleError | null; field: RescheduleError['field'] }) {
  if (!error || error.field !== field) return null;
  return (
    <div id={id} role="alert" style={{ fontSize: '0.8125rem', color: 'var(--admin-danger)', marginTop: '0.25rem' }}>
      {error.message}
    </div>
  );
}

/** Date, slot and capacity inputs of the reschedule modal — only the ones the phase lets you change. */
export default function RescheduleFields({ options, form, onChange, today, error, checking, disabled }: Props) {
  const { editable, slots, capacity } = options;
  const hasError = (f: RescheduleError['field']) => (error?.field === f ? `reschedule-err-${f}` : undefined);

  const slotSelect = (id: string, label: string, value: string, key: 'pickupSlotId' | 'returnSlotId') => (
    <div className="admin-form-group">
      <label className="admin-form-label" htmlFor={id}>
        {label} <span aria-hidden="true" style={{ color: 'var(--admin-danger)' }}>*</span>
      </label>
      <select
        id={id}
        className="admin-select"
        style={{ width: '100%' }}
        value={value}
        onChange={(e) => onChange({ [key]: e.target.value })}
        disabled={disabled}
        required
        aria-invalid={error?.field === 'slot' || undefined}
        aria-describedby={hasError('slot')}
      >
        <option value="">Select a time slot</option>
        {slots.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
      </select>
    </div>
  );

  return (
    <>
      {editable.date && (
        <div className="admin-form-group">
          <label className="admin-form-label" htmlFor="reschedule-date">
            New date <span aria-hidden="true" style={{ color: 'var(--admin-danger)' }}>*</span>
          </label>
          <input
            id="reschedule-date"
            type="date"
            className="admin-input"
            value={form.date}
            min={today}
            onChange={(e) => onChange({ date: e.target.value, garageAgreed: false })}
            disabled={disabled}
            required
            autoFocus
            aria-invalid={error?.field === 'date' || undefined}
            aria-describedby={hasError('date')}
          />
          <FieldError id="reschedule-err-date" error={error} field="date" />
        </div>
      )}

      {editable.pickupSlot && slotSelect('reschedule-pickup-slot', 'Pickup time slot', form.pickupSlotId, 'pickupSlotId')}

      {editable.returnDate && (
        <div className="admin-form-group">
          <label className="admin-form-label" htmlFor="reschedule-return-date">Return date</label>
          <input
            id="reschedule-return-date"
            type="date"
            className="admin-input"
            value={form.returnDate}
            min={editable.date && form.date > today ? form.date : today}
            onChange={(e) => onChange(editable.date ? { returnDate: e.target.value } : { returnDate: e.target.value, garageAgreed: false })}
            disabled={disabled}
            autoFocus={!editable.date}
            aria-invalid={error?.field === 'returnDate' || undefined}
            aria-describedby={hasError('returnDate') ?? 'reschedule-return-help'}
          />
          <div id="reschedule-return-help" style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginTop: '0.25rem' }}>
            Leave empty to return on the booking day.
          </div>
          <FieldError id="reschedule-err-returnDate" error={error} field="returnDate" />
        </div>
      )}

      {editable.returnSlot && slotSelect('reschedule-return-slot', 'Return time slot', form.returnSlotId, 'returnSlotId')}
      <FieldError id="reschedule-err-slot" error={error} field="slot" />

      <div aria-live="polite" data-testid="reschedule-capacity" style={{ fontSize: '0.8125rem', marginBottom: '1rem' }}>
        {checking && <span style={{ color: 'var(--admin-text-muted)' }}>Checking availability…</span>}
        {!checking && capacity && (
          <span style={{ color: capacity.full ? 'var(--admin-warning)' : 'var(--admin-text-muted)' }}>
            {capacity.used} of {capacity.capacity} cars booked on {formatScheduled(capacity.date)}
          </span>
        )}
      </div>

      {!checking && capacity?.full && (
        <div className="admin-info-banner warning" data-testid="reschedule-full-warning">
          The garage is full on this day. Only continue if the garage has agreed to take the car.
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem', cursor: 'pointer', minHeight: '24px' }}>
            <input
              type="checkbox"
              checked={form.garageAgreed}
              onChange={(e) => onChange({ garageAgreed: e.target.checked })}
              disabled={disabled}
              aria-describedby={hasError('capacity')}
            />
            Garage agreed to take the car
          </label>
          <FieldError id="reschedule-err-capacity" error={error} field="capacity" />
        </div>
      )}

      {options.zofeurCovered === false && (
        <div className="admin-info-banner warning" role="status">
          Zofeur does not cover this address, so the order cannot be rescheduled.
        </div>
      )}
    </>
  );
}
